import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Inject, Injectable } from '@angular/core';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';
import { USER_ACTIVATOR, UserActivator } from '@/app/platform/platform-core/user/activators/types';
import { CHART_CLIENT_HEADER, CLIENT_INSTANCE_ID } from '@common/chart-change-signals/chart-change-signals';
import { environment } from '@/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class JwtTokenHttpInterceptor implements HttpInterceptor {
  constructor(
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries,
    @Inject(USER_ACTIVATOR)
    private readonly userActivator: UserActivator
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.mustRenewTokenFirst(req)) {
      return this.interceptUsingUserQueries(req, next);
    }

    // BELANGRIJK: niet weghalen. Een bevroren tab (iOS Safari op de achtergrond, laptop in
    // slaap) mist de refresh-ticks; het JWT (50 min) verloopt intussen. Bij het terugkeren
    // vertrekken meteen verzoeken (visibilitychange, bv. de support-tellers) vóór de eerste
    // tick: de server weigert het verlopen token (401 "Missing Required Jwt UserInfo") en de
    // foutinterceptor toont dat als toast, terwijl de sessie zelf nog prima is. Tot v0.9.0
    // viel dat niet op omdat de backend verlopen tokens per ongeluk aanvaardde (identiteitslek
    // in JwtAuthMiddleware). Tests: jwt-token-http-interceptor.spec.ts ("verouderd token").
    return from(this.userActivator.renewAuthentication()).pipe(
      switchMap(() => this.interceptUsingUserQueries(req, next))
    );
  }

  /** De token-endpoints werken met de refresh-cookie; de refresh zou hier op zichzelf wachten. */
  private mustRenewTokenFirst(req: HttpRequest<any>): boolean {
    const { refreshTokenRoute, logoutRoute } = environment.auth;
    const isTokenEndpoint = [refreshTokenRoute, logoutRoute].some(route => !!route && req.url.endsWith(route));
    return !isTokenEndpoint && this.userQueries.isAuthenticatedUserTokenStale();
  }

  private interceptUsingUserQueries(req: HttpRequest<any>, next: HttpHandler) {
    const token = this.userQueries.getAuthenticatedUserToken();

    if (token) {
      // De client-id laat de server zijn live seintjes aan deze browsertab toeschrijven, zodat
      // die zijn eigen wijzigingen niet als "elders gewijzigd" behandelt (chart-change-signals).
      const reqClone = req.clone({
        headers: new HttpHeaders({ Authorization: token, [CHART_CLIENT_HEADER]: CLIENT_INSTANCE_ID })
      });
      return next.handle(reqClone);
    }

    return next.handle(req);
  }
}
